import React, { useState, useMemo } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Input,
  IconButton,
  Flex,
  Box,
  InputGroup,
  InputLeftElement
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, SearchIcon } from "@chakra-ui/icons";
import { FiCheck, FiX } from "react-icons/fi";
import { ColumnInfo, FormatProto, Header, ObservationColumnProto, ObservationContentProto } from "build-ts/metadata";
import { AggregationType, ColumnType, PolarityEnum } from "build-ts/enums";
import { createMetric } from "services/MetricService";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import showToast from "hooks/useCustomToast";

interface DataModelMenuComponentProps {
  type: "KG_COLUMN" | "METRIC";
  updateFlag?: boolean;
  modelId?: string;
  logicalColumnList: ColumnInfo[];
  kgColumnList?: Header[];
  metricList?: Header[];
  selectedItem: Header | null;
  columnType?: ColumnType;
  columnIndex: number;
  logicalColumn: ColumnInfo;
  onItemSelect: (item: Header | null) => void;
  handleCreateKGColumn: (name: string, columnType: ColumnType, index: number) => Promise<boolean>;
  handleAddMetricCreationRequest: (columnId: string, columnName: string) => void;
  dependentKgColumns?: Map<string, Header[]>;
  placeholder?: string;
}

const DataModelMenuComponent: React.FC<DataModelMenuComponentProps> = React.memo(({
  type,
  updateFlag,
  modelId,
  logicalColumnList,
  kgColumnList,
  metricList,
  selectedItem,
  columnType,
  columnIndex,
  logicalColumn,
  onItemSelect,
  handleCreateKGColumn,
  handleAddMetricCreationRequest,
  dependentKgColumns,
  placeholder = "None",
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Memoize already mapped KG columns to avoid recalculation on every render
  const alreadyMappedKgColumns = useMemo(() => {
    if (type !== "KG_COLUMN" || !dependentKgColumns) return [];
    
    const mappedColumns: Header[] = [];
    
    logicalColumnList.forEach((column, index) => {
      // Skip the current column
      if (index === columnIndex) return;
      
      const kgColumnMapping = dependentKgColumns.get(column.header?.id || "");
      if (kgColumnMapping && kgColumnMapping.length > 0) {
        mappedColumns.push(...kgColumnMapping);
      }
    });
    
    return mappedColumns;
  }, [type, dependentKgColumns, logicalColumnList, columnIndex]);

  // Get the appropriate list based on type
  const itemList = type === "KG_COLUMN" ? kgColumnList : metricList;

  // Filter items based on search term and column type (for KG columns)
  const filteredItems = useMemo(() => {
    if (!itemList) return [];
    
    let filtered = itemList.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // For KG columns, filter by column type if provided
    if (type === "KG_COLUMN" && columnType) {
      filtered = filtered.filter(item => item.column_type === columnType);
    }

    return filtered;
  }, [itemList, searchTerm, type, columnType]);

  const handleCreateNew = async () => {
    if (!newItemName.trim()) return;

    if (type === "KG_COLUMN") {
      // Handle KG Column creation
      const success = await handleCreateKGColumn(newItemName.trim(), columnType!, columnIndex);
      if (success) {
        setIsCreating(false);
        setNewItemName("");
        setSearchTerm("");
      }
    } else if (type === "METRIC") {
      // Add to metric request - this will create the request and add it to metricRequestList
      if(logicalColumnList[columnIndex]?.header?.id && !updateFlag){
        handleAddMetricCreationRequest(logicalColumnList[columnIndex]?.header?.id!, newItemName.trim());
      }
      else if(logicalColumnList[columnIndex]?.header?.id && updateFlag){

        // Create the metric at the same time.
        const request = CreateOrUpdateMetrics_Request.create({
          model_id: modelId,
          metrics: [
            {
              header: Header.create({ name: newItemName.trim(), description: ""}),
              definition: ObservationContentProto.create({
                observation_columns: [ObservationColumnProto.create({
                  logical_column_header: Header.create({ id: logicalColumnList[columnIndex]?.header?.id }),
                  aggregation: AggregationType.SUM,
                })]
              }),
              format: FormatProto.create({
                currency_code: "USD",
                num_decimals: 0,
              }),
              polarity: PolarityEnum.POSITIVE,
            }
          ],
          update_edges: false,
        });
        const response = await createMetric(request);
        if(response){
           // Adding in this state, just for UI to show the new metric name.
          handleAddMetricCreationRequest(logicalColumnList[columnIndex]?.header?.id!, newItemName.trim());
          showToast(`${response.header?.name} Metric created successfully`, "", "success");
        }
      }
      // Reset states
      setIsCreating(false);
      setNewItemName("");
      setSearchTerm("");
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewItemName("");
  };

  if (isCreating) {
    return (
      <Flex alignItems="center" width="180px">
        <Input
          placeholder={`Enter ${type === "KG_COLUMN" ? "KG column" : "metric"} name`}
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          size="sm"
          mr={1}
          flex={1}
          onKeyPress={(e) => e.key === "Enter" && handleCreateNew()}
        />
        <IconButton
          aria-label={`Create ${type === "KG_COLUMN" ? "KG Column" : "Metric"}`}
          icon={<FiCheck />}
          size="xs"
          colorScheme="green"
          variant="ghost"
          onClick={handleCreateNew}
          isDisabled={!newItemName.trim()}
        />
        <IconButton
          aria-label="Cancel"
          icon={<FiX />}
          size="xs"
          variant="ghost"
          onClick={handleCancelCreate}
        />
      </Flex>
    );
  }

  return (
    <Menu placement="bottom-start" closeOnSelect={true}>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        size="sm"
        variant="outline"
        textAlign="left"
        width="170px"
        justifyContent="space-between"
        overflow="hidden"
      >
        <Text 
          isTruncated 
          maxWidth="120px"
          title={selectedItem?.name || placeholder}
          m={0}
        >
          {selectedItem?.name || placeholder}
        </Text>
      </MenuButton>
      <MenuList maxHeight="250px" overflowY="auto" zIndex={1000}>
        {/* Search Bar */}
        <Box 
          px={3} 
          pb={2}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={`Search ${type === "KG_COLUMN" ? "KG columns" : "metrics"}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outline"
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus={false}
            />
          </InputGroup>
        </Box>

        {/* None option */}
        {type === "KG_COLUMN" && (
          <MenuItem onClick={() => onItemSelect(null)}>
            None
          </MenuItem>
        )}

        {/* Create New option */}
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            setIsCreating(true);
            setNewItemName(logicalColumn.header?.name || "");
          }}
          closeOnSelect={false}
          icon={<AddIcon />}
          color="purple.600"
          fontWeight="bold"
        >
          Create New {type === "KG_COLUMN" ? "KG Column" : "Metric"}
        </MenuItem>

         {/* Filtered Items */}
         {filteredItems?.length && filteredItems?.length > 0 ? (
           filteredItems?.map((item) => {
             // Check if this KG column is already mapped to another logical column (not the current one)
             const isAlreadyMapped = type === "KG_COLUMN" && 
               alreadyMappedKgColumns.some(mappedColumn => mappedColumn.id === item.id) &&
               selectedItem?.id !== item.id; // Allow current selection to remain enabled
             
             return (
               <MenuItem 
                 key={item.id} 
                 onClick={() => {
                   if (!isAlreadyMapped) {
                     onItemSelect(item);
                   }
                 }}
                 isDisabled={isAlreadyMapped}
                 opacity={isAlreadyMapped ? 0.5 : 1}
                 cursor={isAlreadyMapped ? "not-allowed" : "pointer"}
                 bg={selectedItem?.id === item.id ? "purple.100" : "surface.default"}
               >
                 {item.name}
               </MenuItem>
             );
           })
        ) : (
          searchTerm && (
            <MenuItem isDisabled>
              No {type === "KG_COLUMN" ? "KG columns" : "metrics"} found
            </MenuItem>
          )
        )}
      </MenuList>
    </Menu>
  );
});

export default DataModelMenuComponent;